import { useState, useEffect, useCallback, useRef } from 'react'
import { cpGetPreviewToken } from '@/lib/api/controlPlaneClient'

const REFRESH_INTERVAL = 8 * 60 * 1000 // 8 minutes
const RETRY_DELAY = 30_000 // 30 seconds
const MAX_RETRIES = 3

interface UsePreviewTokenResult {
  token: string | null
  isLoading: boolean
  error: boolean
  retry: () => void
}

export function usePreviewToken(enabled: boolean): UsePreviewTokenResult {
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const retryCount = useRef(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchToken = useCallback(async () => {
    try {
      const result = await cpGetPreviewToken()
      setToken(result.token)
      setError(false)
      setIsLoading(false)
      retryCount.current = 0
    } catch {
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current++
        retryTimeoutRef.current = setTimeout(fetchToken, RETRY_DELAY)
      } else {
        setError(true)
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    fetchToken()
    intervalRef.current = setInterval(() => {
      retryCount.current = 0
      fetchToken()
    }, REFRESH_INTERVAL)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
    }
  }, [enabled, fetchToken])

  const retry = useCallback(() => {
    retryCount.current = 0
    setError(false)
    setIsLoading(true)
    fetchToken()
  }, [fetchToken])

  return { token, isLoading, error, retry }
}
